import { c as _c } from "react/compiler-runtime";
import { FaroSwitch } from "@components/common/faro-switch";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { FontWeights, Text, Tooltip } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { IntegrationTileError } from "@pages/project-details/project-integrations/integration-tile-error";
import { IntegrationTileSkeleton } from "@pages/project-details/project-integrations/integration-tile-skeleton";
import { INTEGRATION_TILE_WIDTH } from "@pages/project-details/project-integrations/project-integrations-constants";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { withEllipsis } from "@styles/common-styles";
import { cardProperties } from "@utils/ui-utils";
import { useCallback, useEffect, useState } from "react";
import { PALETTE_VARS } from "@styles/palette-vars";
/** Renders a project integration tile */
export function IntegrationTile(t0) {
  const $ = _c(60);
  const {
    integrationId,
    onActionButtonClick,
    integrationsService,
    setProjectIntegrations,
    projectIntegrations
  } = t0;
  const IntegrationIcon = INTEGRATION_INFO[integrationId].icon;
  const {
    handleErrorWithToast
  } = useErrorContext();
  const [hasCheckedDataFormat, setHasCheckedDataFormat] = useState(false);
  const [isUpdateError, setIsUpdateError] = useState(false);
  const projectName = projectIntegrations?.[integrationId]?.projectName;
  let t1;
  if ($[0] !== integrationId || $[1] !== integrationsService || $[2] !== projectIntegrations) {
    t1 = !!projectIntegrations && integrationsService.isConnectedInXGFormat(integrationId, projectIntegrations);
    $[0] = integrationId;
    $[1] = integrationsService;
    $[2] = projectIntegrations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isConnectedInXGFormat = t1;
  let t2;
  if ($[4] !== integrationId || $[5] !== integrationsService || $[6] !== projectIntegrations) {
    t2 = !!projectIntegrations && integrationsService.isConnectedInLegacyFormat(integrationId, projectIntegrations);
    $[4] = integrationId;
    $[5] = integrationsService;
    $[6] = projectIntegrations;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const isConnectedInLegacyFormat = t2;
  const isConnectionOutdated = isConnectedInLegacyFormat && !isConnectedInXGFormat;
  const isError = isUpdateError && isConnectionOutdated;
  let t3;
  if ($[8] !== handleErrorWithToast || $[9] !== integrationId || $[10] !== integrationsService || $[11] !== setProjectIntegrations) {
    t3 = async data => {
      ;
      try {
        const response = await integrationsService.updateDataFormat(integrationId, data);
        setProjectIntegrations(response);
      } catch (t4) {
        const error = t4;
        handleErrorWithToast({
          id: `updateDataFormat-${Date.now().toString()}`,
          title: "Failed to update the connection data format.",
          error
        });
        setIsUpdateError(true);
      }
    };
    $[8] = handleErrorWithToast;
    $[9] = integrationId;
    $[10] = integrationsService;
    $[11] = setProjectIntegrations;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const updateDataFormat = t3;
  let t4;
  if ($[13] !== isConnectionOutdated || $[14] !== projectIntegrations || $[15] !== updateDataFormat) {
    t4 = async () => {
      if (isConnectionOutdated) {
        await updateDataFormat(projectIntegrations);
      }
      setHasCheckedDataFormat(true);
    };
    $[13] = isConnectionOutdated;
    $[14] = projectIntegrations;
    $[15] = updateDataFormat;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const checkDataFormat = t4;
  let t5;
  let t6;
  if ($[17] !== checkDataFormat || $[18] !== hasCheckedDataFormat || $[19] !== projectIntegrations) {
    t5 = () => {
      if (!hasCheckedDataFormat && projectIntegrations !== null) {
        checkDataFormat();
      }
    };
    t6 = [checkDataFormat, hasCheckedDataFormat, projectIntegrations];
    $[17] = checkDataFormat;
    $[18] = hasCheckedDataFormat;
    $[19] = projectIntegrations;
    $[20] = t5;
    $[21] = t6;
  } else {
    t5 = $[20];
    t6 = $[21];
  }
  useEffect(t5, t6);
  if (!hasCheckedDataFormat) {
    let t7;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <IntegrationTileSkeleton />;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  const t7 = isError ? undefined : "200px";
  const t8 = isError ? "200px" : undefined;
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = {
      width: INTEGRATION_TILE_WIDTH,
      height: t7,
      minHeight: t8,
      marginTop: cardProperties.marginTop,
      boxShadow: cardProperties.boxShadow,
      borderRadius: cardProperties.borderRadius,
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between"
    };
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: 1
    };
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      width: "50px",
      height: "50px"
    };
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== IntegrationIcon) {
    t12 = <IntegrationIcon sx={t11} />;
    $[28] = IntegrationIcon;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      color: "text.primary"
    };
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = INTEGRATION_INFO[integrationId];
  let t15;
  if ($[31] !== t14.displayName) {
    t15 = <Text variant="heading16" sx={t13}>{t14.displayName}</Text>;
    $[31] = t14.displayName;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t12 || $[34] !== t15) {
    t16 = <Box sx={t10}>{t12}{t15}</Box>;
    $[33] = t12;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== integrationId || $[37] !== isError) {
    t17 = isError ? <IntegrationTileError /> : <Text variant="bodyM" component="div" sx={{
      color: "text.secondary"
    }}>{INTEGRATION_INFO[integrationId].description}</Text>;
    $[36] = integrationId;
    $[37] = isError;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16 || $[40] !== t17) {
    t18 = <Box>{t16}{t17}</Box>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = projectName ? "space-between" : "flex-end";
  let t20;
  if ($[42] !== t19) {
    t20 = {
      borderTop: `1px solid ${PALETTE_VARS.divider}`,
      textAlign: "right",
      pt: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: t19,
      height: "34px"
    };
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== projectName) {
    t21 = projectName && <Tooltip component="var" title={projectName} sx={{
      display: "block",
      ...withEllipsis,
      padding: "8px 0px",
      fontWeight: FontWeights.Regular
    }}><Text variant="heading12" component="div" sx={{
        lineHeight: "18px",
        ...withEllipsis
      }}>Synced with<Box component="var" sx={{
          ml: "4px"
        }}>{projectName}</Box></Text></Tooltip>;
    $[44] = projectName;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = isConnectedInXGFormat || isConnectedInLegacyFormat;
  let t23;
  if ($[46] !== integrationId || $[47] !== onActionButtonClick) {
    t23 = () => onActionButtonClick(integrationId);
    $[46] = integrationId;
    $[47] = onActionButtonClick;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t22 || $[50] !== t23) {
    t24 = <FaroSwitch checked={t22} onChange={t23} tooltipTextNotChecked="Enable" tooltipTextChecked="Disable" />;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t20 || $[53] !== t21 || $[54] !== t24) {
    t25 = <Box sx={t20}>{t21}{t24}</Box>;
    $[52] = t20;
    $[53] = t21;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t18 || $[57] !== t25 || $[58] !== t9) {
    t26 = <Box component="div" sx={t9}>{t18}{t25}</Box>;
    $[56] = t18;
    $[57] = t25;
    $[58] = t9;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  return t26;
}